import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ModalTheme.css';
import './LendModal.css';

// "Who has it, and since when."
//
// The two facts a lend needs are both already known most of the time: Steam
// recorded who the item went to and when it went. So the modal opens with the
// answer filled in and the friend list pre-sorted, and the common case is one
// click on Confirm. Typing a name is the fallback, not the flow.

const todayISO = () => new Date().toISOString().slice(0, 10);

/** ISO date input value -> full ISO timestamp, keeping a known time of day. */
function toISO(dateStr, fallbackISO) {
  if (!dateStr) return null;
  // A lend imported from a trade knows the exact moment. If the user has not
  // moved the date off that day, keep the real timestamp rather than flattening
  // it to midnight.
  if (fallbackISO && fallbackISO.slice(0, 10) === dateStr) return fallbackISO;
  return new Date(`${dateStr}T12:00:00`).toISOString();
}

/** date input value + n days, back as a date input value. */
const addDays = (dateStr, n) => {
  const d = new Date(`${dateStr || todayISO()}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const daysBetween = (a, b) => {
  if (!a || !b) return null;
  return Math.round((new Date(`${b}T12:00:00`) - new Date(`${a}T12:00:00`)) / 86400000);
};

// A lend is almost always "a few days" or "until he is done with it". Four
// buttons cover the first case; the date field is still there for the rest.
const DUE_PRESETS = [
  { label: 'Open ended', days: null },
  { label: '1 week',     days: 7 },
  { label: '2 weeks',    days: 14 },
  { label: '1 month',    days: 30 },
];

/** Two letters and a stable colour, so a name is recognisable at a glance. */
const initialsOf = name => (name || '')
  .trim().split(/\s+/).slice(0, 2)
  .map(w => [...w][0] || '')
  .join('')
  .toUpperCase() || '?';

const hueOf = name => {
  let h = 0;
  for (const ch of (name || '')) h = (h * 31 + ch.codePointAt(0)) % 360;
  return h;
};

function Avatar({ name, size = 'md' }) {
  return (
    <span className={`lnd-av ${size}`} style={{ '--av-hue': hueOf(name) }} aria-hidden="true">
      {initialsOf(name)}
    </span>
  );
}

function Section({ step, title, hint, tone = '', children }) {
  return (
    <section className={`lnd-sec ${tone}`}>
      <header className="lnd-sec-head">
        <span className="lnd-step">{step}</span>
        <div className="lnd-sec-txt">
          <h4 className="lnd-sec-title">{title}</h4>
          {hint && <p className="lnd-sec-hint">{hint}</p>}
        </div>
      </header>
      {children}
    </section>
  );
}

export default function LendModal({
  items = [],
  newItems = [],
  friends = [],
  suggested = null,
  defaultAt = null,
  variant = 'classic',
  onConfirm,
  onClose,
}) {
  // Items rebuilt from the trade history have no cost basis, because Steam
  // records what moved and never what it cost. Left blank the row would carry
  // $0, and the whole sale price would read as profit the day it sells. So the
  // price is asked for here, once, with an explicit way to say you do not know.
  const [prices, setPrices] = useState(() =>
    Object.fromEntries(newItems.map(c => [c.key, ''])));
  const [partner, setPartner] = useState(suggested || null);
  const [typed,   setTyped]   = useState('');
  const [at,      setAt]      = useState((defaultAt || new Date().toISOString()).slice(0, 10));
  const [dueAt,   setDueAt]   = useState('');
  const [note,    setNote]    = useState('');
  const [query,   setQuery]   = useState('');
  const panelRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const esc = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', esc); };
  }, [onClose]);

  const shortlist = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? friends.filter(f => f.name.toLowerCase().includes(q)) : friends;
    return list.slice(0, q ? 30 : 9);
  }, [friends, query]);

  // A typed name only counts once it is not blank. Picking from the list always
  // wins, because it carries the steamId64 and profile link a typed name cannot.
  const chosen = partner || (typed.trim() ? { name: typed.trim(), profileUrl: null, steamId64: null } : null);

  const confirm = () => {
    if (!chosen) return;
    onConfirm({
      to:    chosen,
      at:    toISO(at, defaultAt) || new Date().toISOString(),
      dueAt: toISO(dueAt, null),
      note:  note.trim(),
      prices,
    });
  };

  const total  = items.length + newItems.length;
  const plural = total !== 1;

  // What the loan is worth at today's market, when the caller knew it. Not a
  // number that moves any total: it is here so the size of what is leaving the
  // inventory is visible before confirming.
  const valueOut = items.reduce((s, it) => s + (Number(it.value) || 0), 0);

  const dueDays = daysBetween(at, dueAt);

  return (
    <div className={`mdl-overlay ${variant} top`} onMouseDown={e => { if (!panelRef.current?.contains(e.target)) onClose(); }}>
      <div className="mdl-panel lnd-panel" ref={panelRef}>
        <div className="mdl-head lnd-head">
          <div className="mdl-head-txt">
            <h3 className="mdl-title lnd-big-title">Lend {total > 1 ? `${total} items` : 'an item'}</h3>
            <p className="mdl-sub">
              Still yours, just not in your inventory. Cost basis and portfolio value are untouched.
            </p>
          </div>
          {valueOut > 0 && (
            <div className="lnd-head-stat">
              <span className="k">Going out</span>
              <span className="v">${valueOut.toFixed(2)}</span>
            </div>
          )}
          <button type="button" className="mdl-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="mdl-body lnd-body">
          {/* ── What is going out ─────────────────────── */}
          {items.length > 0 && (
            <Section step="1" title={items.length > 1 ? `${items.length} items leaving` : 'What is leaving'}>
              <div className="lnd-items">
                {items.map(it => (
                  <div className="lnd-item" key={it.key}>
                    {it.image
                      ? <img src={it.image} alt="" />
                      : <span className="lnd-item-blank" />}
                    <span className="lnd-item-txt">
                      <span className="lnd-item-name">{it.name}</span>
                      {it.subtitle && <span className="lnd-item-sub">{it.subtitle}</span>}
                    </span>
                    {Number(it.value) > 0 && (
                      <span className="lnd-item-val">${Number(it.value).toFixed(2)}</span>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* ── Cost basis for rebuilt rows ───────────── */}
          {newItems.length > 0 && (
            <Section
              step={items.length > 0 ? '2' : '1'}
              tone="caution"
              title={`What did ${newItems.length > 1 ? 'these' : 'this'} cost you?`}
              hint={`${newItems.length > 1 ? 'These are' : 'This is'} being created from your Steam trade
                history, which records what moved but never what it cost. Leave a price blank if you
                do not know: the row is created at $0 and flagged, rather than pretending the whole
                sale price is profit later.`}>
              <div className="lnd-price-rows">
                {newItems.map(c => (
                  <div className="lnd-price-row" key={c.key}>
                    <span className="lnd-price-name">
                      {c.name}
                      <span className="lnd-item-sub">
                        {c.condition || 'n/a'}
                        {c.at ? ` · ${c.direction === 'sent' ? 'sent' : 'arrived'} ${new Date(c.at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}` : ''}
                        {c.partner ? ` · ${c.direction === 'sent' ? 'to' : 'from'} ${c.partner.name}` : ''}
                      </span>
                    </span>
                    <span className="lnd-price-input">
                      <span className="lnd-price-sign">$</span>
                      <input type="number" min="0" step="0.01" placeholder="unknown"
                        className="mdl-input"
                        value={prices[c.key] ?? ''}
                        onChange={e => setPrices(p => ({ ...p, [c.key]: e.target.value }))} />
                    </span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* ── Who ───────────────────────────────────── */}
          <Section
            step={items.length > 0 && newItems.length > 0 ? '3' : items.length > 0 || newItems.length > 0 ? '2' : '1'}
            title="Who has it"
            hint={suggested
              ? <>Steam recorded this going to <strong>{suggested.name}</strong>, so that is the default.</>
              : friends.length
                ? 'People you trade with most and most recently come first.'
                : 'No trade history yet, so there is nobody to suggest. Type a name.'}>

            {chosen && (
              <div className="lnd-chosen">
                <Avatar name={chosen.name} size="lg" />
                <div className="lnd-chosen-txt">
                  <span className="lnd-chosen-name">{chosen.name}</span>
                  <span className="lnd-chosen-meta">
                    {chosen.steamId64 ? <span className="lnd-verified">verified from Steam</span> : 'typed by hand'}
                    {chosen.lends > 0 ? ` · ${chosen.lends} previous lend${chosen.lends !== 1 ? 's' : ''}` : ''}
                    {chosen.trades > 0 ? ` · ${chosen.trades} trade${chosen.trades !== 1 ? 's' : ''} together` : ''}
                  </span>
                </div>
                <button type="button" className="mdl-btn sm"
                  onClick={() => { setPartner(null); setTyped(''); }}>
                  Change
                </button>
              </div>
            )}

            {!chosen && (
              <div className="lnd-who">
                {friends.length > 0 && (
                  <input className="mdl-input" placeholder="Search people you have traded with…"
                    value={query} onChange={e => setQuery(e.target.value)} />
                )}
                <div className="lnd-friends">
                  {shortlist.map(f => (
                    <button type="button" key={f.steamId64 || f.name} className="lnd-friend"
                      onClick={() => setPartner(f)}>
                      <Avatar name={f.name} />
                      <span className="lnd-friend-txt">
                        <span className="lnd-friend-name">{f.name}</span>
                        <span className="lnd-friend-meta">
                          {f.lends > 0 ? `${f.lends} lend${f.lends !== 1 ? 's' : ''} · ` : ''}
                          {f.trades} trade{f.trades !== 1 ? 's' : ''}
                        </span>
                      </span>
                    </button>
                  ))}
                  {friends.length > 0 && shortlist.length === 0 && (
                    <span className="lnd-none">Nobody by that name in your trade history.</span>
                  )}
                </div>
                <div className="lnd-typed">
                  <span className="mdl-label">Not in the list?</span>
                  <input className="mdl-input" placeholder="Type a name"
                    value={typed} onChange={e => setTyped(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && typed.trim()) confirm(); }} />
                </div>
              </div>
            )}
          </Section>

          {/* ── When & note ───────────────────────────── */}
          <div className="lnd-grid2">
            <Section step="⏱" title="Dates" hint="A due date only flags the loan as overdue. Nothing else reads it.">
              <div className="lnd-dates">
                <div>
                  <span className="mdl-label">Lent on</span>
                  <input type="date" className="mdl-input" value={at} max={todayISO()}
                    onChange={e => setAt(e.target.value)} />
                </div>
                <div>
                  <span className="mdl-label">Expected back</span>
                  <input type="date" className="mdl-input" value={dueAt} min={at}
                    onChange={e => setDueAt(e.target.value)} />
                </div>
              </div>
              <div className="lnd-presets">
                {DUE_PRESETS.map(p => {
                  const on = p.days == null ? !dueAt : dueAt === addDays(at, p.days);
                  return (
                    <button type="button" key={p.label} className={`mdl-pill ${on ? 'on' : ''}`}
                      onClick={() => setDueAt(p.days == null ? '' : addDays(at, p.days))}>
                      {p.label}
                    </button>
                  );
                })}
              </div>
              <p className="lnd-sec-hint">
                {dueDays == null
                  ? 'Open ended: it will never be marked overdue.'
                  : `Due back ${dueDays} day${dueDays !== 1 ? 's' : ''} after it goes out.`}
              </p>
            </Section>

            <Section step="✎" title="Note" hint="Optional. Shown on the loan card so you remember why it went out.">
              <input className="mdl-input" placeholder="e.g. for his tournament team"
                value={note} onChange={e => setNote(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && chosen) confirm(); }} />
            </Section>
          </div>
        </div>

        <div className="mdl-foot lnd-foot">
          <span className="mdl-foot-note">
            {total} item{plural ? 's' : ''} stay{plural ? '' : 's'} in your totals
            {newItems.length > 0 ? ` · ${newItems.length} row${newItems.length !== 1 ? 's' : ''} created` : ''}
          </span>
          <button type="button" className="mdl-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="mdl-btn primary" disabled={!chosen} onClick={confirm}>
            {chosen ? `Lend to ${chosen.name}` : 'Pick someone'}
          </button>
        </div>
      </div>
    </div>
  );
}
